import type { PointsPreviewResult } from './PointsPreviewPane';
import type { PointsSystemRow, PointsSystemVersionRow } from './PointsSystemList';

/**
 * §9.8's compare affordance: the draft's board against the previously published one.
 *
 * <b>This is how an operator judges whether an edit does what they intended.</b> A points system
 * is not judged by reading its definition — nobody can look at a placement table and say what
 * moving 8th place from 26 to 24 does to a season. They can look at "Ariel Rosello moves 4th → 2nd"
 * and say whether that is what they meant.
 *
 * Two rules the arithmetic has to respect, and both are about the preview's row limit rather than
 * about scoring:
 *
 * 1. <b>A player missing from a truncated board is unknown, not new.</b> Both boards are the top N
 *    of a longer list. Somebody who has climbed into the draft's top 100 from 140th under the
 *    published version is not "new to the board" — they were simply below where the previous list
 *    stopped. Reporting that as new would be the confidently-wrong number this codebase exists to
 *    avoid; `unknown` says the honest thing.
 * 2. <b>"No longer scores" can only be counted when the draft's own board is complete.</b> If the
 *    current board is itself cut at 100, a player who fell off the *list* looks identical to one
 *    who fell off the *board*, so the count is null and the pane says nothing rather than guessing.
 *
 * Points are compared at the precision the preview reports them in — `points` on a preview row is
 * already `DisplayPoints`, rounded to the definition's `decimalPlaces` — so this never reports a
 * change nobody can see. Ranking on both sides was done on the unrounded figures by the engine.
 */

/**
 * What §9.8's compare measures against: the newest published version *below* the one on screen.
 *
 * Below, rather than the newest published outright, because the version on screen is not always
 * the newest. For a draft the two agree — a draft is always the highest number, so every published
 * version is below it. But somebody reading published v2 while v4 exists is asking what v2
 * changed, and comparing it with v4 would answer a question about the future of a frozen document.
 *
 * Archived versions are left out. One was published once, but it is no longer offered to a new
 * leaderboard, so it is not what an edit is being judged against.
 */
export function previousPublished(
  system: PointsSystemRow,
  versionNumber: number,
): PointsSystemVersionRow | null {
  const earlier = system.versions.filter(
    (v) => v.status === 'published' && v.versionNumber < versionNumber,
  );
  return earlier.length === 0
    ? null
    : earlier.reduce((a, b) => (b.versionNumber > a.versionNumber ? b : a));
}

export type ComparisonBaseline =
  | {
      /** The version to score alongside. */
      version: PointsSystemVersionRow;
      /** Short form, for the table's column header: "v2". */
      short: string;
      /** Long form, for the checkbox: "version 2, published 4 Aug 2026". */
      long: string;
      reason: null;
    }
  /** Nothing to compare with, and `reason` says which of the two ways that happened. */
  | { version: null; short: null; long: null; reason: string };

/**
 * The compare control's labels, or the sentence that replaces it.
 *
 * Both consoles ask the same question of the same catalogue shape, so the answer is written once.
 * The two "nothing to compare with" cases are told apart because they mean different things to an
 * operator: one is "publish something and this becomes useful", the other is "there is nothing
 * earlier, and there never will be for this version".
 */
export function comparisonBaseline(
  system: PointsSystemRow,
  versionNumber: number,
): ComparisonBaseline {
  const version = previousPublished(system, versionNumber);

  if (!version) {
    const anyPublished = system.versions.some((v) => v.status === 'published');
    return {
      version: null,
      short: null,
      long: null,
      reason: anyPublished
        ? `Nothing to compare with — version ${versionNumber} is the earliest published version of this system.`
        : 'Nothing to compare with yet — no version of this system has been published.',
    };
  }

  const on = publishedOn(version.publishedAt);
  return {
    version,
    short: `v${version.versionNumber}`,
    long: `version ${version.versionNumber}${on ? `, published ${on}` : ''}`,
    reason: null,
  };
}

function publishedOn(timestamp: string | null): string | null {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export type PointsMovementStatus =
  /** Climbed the board. */
  | 'up'
  /** Fell. */
  | 'down'
  /** Same rank. Their points may still differ — see `pointsDelta`. */
  | 'level'
  /** Not scored at all by the published version, whose board was complete. */
  | 'new'
  /** Not on the published version's *list*, which was cut short. Where they were is not knowable. */
  | 'unknown';

export interface PointsMovement {
  status: PointsMovementStatus;
  /** Places gained; positive is up the board. Null when the previous rank isn't known. */
  places: number | null;
  previousRank: number | null;
  previousPoints: number | null;
  /** Current minus previous, at display precision. Null when there is nothing to subtract from. */
  pointsDelta: number | null;
}

export interface StandingsComparison {
  /** Movement for each player on the current board, by player id. */
  movements: ReadonlyMap<string, PointsMovement>;
  up: number;
  down: number;
  /** Same rank as before. */
  level: number;
  /** On this board, absent from a published board that listed everybody. */
  entered: number;
  /** Scored by the published version and not by this one. Null when this board is truncated. */
  left: number | null;
  /** On this board, and off the end of a truncated published list. */
  unknown: number;
  /** Matched players whose points changed, whether or not their rank did. */
  pointsChanged: number;
}

export function compareStandings(
  current: PointsPreviewResult,
  previous: PointsPreviewResult,
): StandingsComparison {
  const before = new Map(previous.rows.map((row) => [row.playerId, row]));
  // Both boards report the whole field in `playersRanked` and only the top `limit` in `rows`.
  const previousTruncated = previous.playersRanked > previous.rows.length;
  const currentTruncated = current.playersRanked > current.rows.length;

  const movements = new Map<string, PointsMovement>();
  let up = 0;
  let down = 0;
  let level = 0;
  let entered = 0;
  let unknown = 0;
  let pointsChanged = 0;

  for (const row of current.rows) {
    const was = before.get(row.playerId);

    if (!was) {
      const status: PointsMovementStatus = previousTruncated ? 'unknown' : 'new';
      if (status === 'new') entered += 1;
      else unknown += 1;
      movements.set(row.playerId, {
        status,
        places: null,
        previousRank: null,
        previousPoints: null,
        pointsDelta: null,
      });
      continue;
    }

    const places = was.rank - row.rank;
    const status: PointsMovementStatus = places > 0 ? 'up' : places < 0 ? 'down' : 'level';
    if (status === 'up') up += 1;
    else if (status === 'down') down += 1;
    else level += 1;

    const pointsDelta = round(row.points - was.points);
    if (pointsDelta !== 0) pointsChanged += 1;

    movements.set(row.playerId, {
      status,
      places,
      previousRank: was.rank,
      previousPoints: was.points,
      pointsDelta,
    });
  }

  const shown = new Set(current.rows.map((row) => row.playerId));
  const left = currentTruncated
    ? null
    : previous.rows.filter((row) => !shown.has(row.playerId)).length;

  return { movements, up, down, level, entered, left, unknown, pointsChanged };
}

/**
 * The one-line answer to "did my edit do what I meant".
 *
 * <b>A board where nothing moved but every score changed is the case worth wording carefully.</b>
 * Doubling every row of a placement table changes no rank at all, and an operator told only "no
 * rank changed" would reasonably conclude their edit had not taken effect.
 */
export function summariseComparison(comparison: StandingsComparison): string {
  const clauses: string[] = [];

  if (comparison.up > 0) clauses.push(`${comparison.up} moved up`);
  if (comparison.down > 0) clauses.push(`${comparison.down} moved down`);
  if (comparison.entered > 0) {
    clauses.push(`${comparison.entered} ${comparison.entered === 1 ? 'is' : 'are'} new to the board`);
  }
  if (comparison.left !== null && comparison.left > 0) {
    clauses.push(`${comparison.left} no longer ${comparison.left === 1 ? 'scores' : 'score'}`);
  }
  if (comparison.unknown > 0) {
    clauses.push(
      `${comparison.unknown} ${comparison.unknown === 1 ? 'was' : 'were'} below where the published list stopped`,
    );
  }

  if (clauses.length === 0) {
    return comparison.pointsChanged > 0
      ? `Every rank is the same, but ${comparison.pointsChanged} ${
          comparison.pointsChanged === 1 ? 'player scores' : 'players score'
        } differently.`
      : 'Identical — same order, same scores.';
  }

  return `${sentence(clauses)}.`;
}

function sentence(clauses: string[]): string {
  if (clauses.length === 1) return capitalise(clauses[0]!);
  const last = clauses[clauses.length - 1]!;
  return `${capitalise(clauses.slice(0, -1).join(', '))} and ${last}`;
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Two display figures subtracted can land on 0.30000000000000004; nobody wants to read that. */
function round(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}
